import React, { useState, useEffect, useRef } from 'react';
import {
  getWeb20Prefill, generateWeb20Articles,
  getWeb20CycleStatus, getLatestWeb20Cycle,
  auditWeb20Submissions, getWeb20AuditHistory, publishWeb20Article, completeWeb20Article,
  updateWeb20ArticleContent, downloadS3ImageProxy,
  getWeb20Library,
  type Web20Article, type Web20AuditResult, type Web20TrackedAudit,
} from '../../../services/web2Service';
import { initiateWeb20Scan, getWeb20ScanStatus } from '../../../services/discoveryService';

const PLATFORM_COLORS: Record<string, string> = {
  'Sites Google': 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 border-blue-100 dark:border-blue-800',
  'Medium': 'bg-gray-50 dark:bg-gray-800/50 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700',
  'WordPress.com': 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 border-indigo-100 dark:border-indigo-800',
  'Blogger': 'bg-orange-50 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 border-orange-100 dark:border-orange-800',
  'Weebly': 'bg-teal-50 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400 border-teal-100 dark:border-teal-800',
};

const PLATFORM_ICONS: Record<string, string> = {
  'Sites Google': 'ri-google-line',
  'Medium': 'ri-medium-line',
  'WordPress.com': 'ri-wordpress-line',
  'Blogger': 'ri-blogger-line',
  'Weebly': 'ri-layout-masonry-line',
};
import ArticleDrawer from './ArticleDrawer';

interface Props { onBack: () => void; darkMode?: boolean; userId: string; }

const isValidUrl = (u: string) => { try { new URL(u); return true; } catch { return false; } };

export default function Web2ArticlePage({ onBack, darkMode, userId }: Props) {
  const [prefill, setPrefill] = useState<any>(null);
  const [selectedKeyword, setSelectedKeyword] = useState<string | null>(null);
  const [cycleId, setCycleId] = useState<string | null>(null);
  const [cycleStatus, setCycleStatus] = useState<string>('idle');
  const [article, setArticle] = useState<Web20Article | null>(null);
  const [libraryListings, setLibraryListings] = useState<Web20Article[]>([]);
  const [libraryLoading, setLibraryLoading] = useState(false);
  const [generatedArticleId, setGeneratedArticleId] = useState<string | null>(null);
  const [hasDefaultSelected, setHasDefaultSelected] = useState(false);
  const [auditUrls, setAuditUrls] = useState('');
  const [auditResults, setAuditResults] = useState<Web20AuditResult[]>([]);
  const [auditHistory, setAuditHistory] = useState<Web20TrackedAudit[]>([]);
  const [auditing, setAuditing] = useState(false);
  const [drawerArticle, setDrawerArticle] = useState<Web20Article | null>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [checkedPlatforms, setCheckedPlatforms] = useState<Record<string, boolean>>({});
  const [isMarkingComplete, setIsMarkingComplete] = useState(false);
  const [markCompleteSuccess, setMarkCompleteSuccess] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Edit Article State
  const [isEditingArticle, setIsEditingArticle] = useState(false);
  const [editArticleTitle, setEditArticleTitle] = useState('');
  const [editArticleContent, setEditArticleContent] = useState('');
  const [savingArticle, setSavingArticle] = useState(false);
  const [articleSaveError, setArticleSaveError] = useState<string | null>(null);
  const [generateError, setGenerateError] = useState<string | null>(null);
  const prevArticleRef = useRef<Web20Article | null>(null);

  // New/Existing Website State and Discovery Audit State
  const [websiteState, setWebsiteState] = useState<'new_website' | 'existing_website'>('new_website');
  const [discoveredAssets, setDiscoveredAssets] = useState<any[]>([]);
  const [isAuditing, setIsAuditing] = useState(false);
  const [auditScanId, setAuditScanId] = useState<string | null>(null);
  const [auditCompleted, setAuditCompleted] = useState(false);

  const dm = darkMode;
  const card = dm ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200';

  const fetchLibrary = async () => {
    setLibraryLoading(true);
    try {
      const res = await getWeb20Library(userId);
      setLibraryListings(res.library || []);
    } catch (e) {
      console.error('Failed to load library:', e);
    } finally {
      setLibraryLoading(false);
    }
  };

  useEffect(() => {
    Promise.all([
      getWeb20Prefill(userId).catch(() => null),
      getWeb20AuditHistory(userId).catch(() => ({ results: [] })),
      getWeb20Library(userId).catch(() => ({ library: [] })),
      getLatestWeb20Cycle(userId).catch(() => null),
    ]).then(([pf, hist, lib, latestCycle]) => {
      if (pf) {
        setPrefill(pf);
        if (pf.keywords?.length > 0) setSelectedKeyword(pf.primary_keyword || pf.keywords[0]);
        setWebsiteState(pf.is_new_website ? 'new_website' : 'existing_website');
      }
      if (hist) setAuditHistory(hist.results || []);
      
      const library = lib?.library || [];
      setLibraryListings(library);

      if (latestCycle?.status === 'processing') {
        setCycleId(latestCycle.cycle_id);
        setCycleStatus('processing');
        setGenerating(true);
        startPoll(latestCycle.cycle_id);
      } else if (library.length > 0 && !hasDefaultSelected) {
        const latest = library[0];
        setArticle(latest);
        setGeneratedArticleId(latest.id);
        setHasDefaultSelected(true);
      }
      
      setLoading(false);
    });
  }, [userId]);

  // Load checked platforms from article's published_platforms
  useEffect(() => {
    if (article) {
      const initialChecked: Record<string, boolean> = {};
      if (article.published_platforms) {
        article.published_platforms.forEach((p: string) => {
          initialChecked[p] = true;
        });
      }
      setCheckedPlatforms(initialChecked);
    } else {
      setCheckedPlatforms({});
    }
  }, [article]);
  // Poll for discovery audit results
  useEffect(() => {
    if (!auditScanId || !isAuditing) return;
    const interval = setInterval(async () => {
      try {
        const statusData = await getWeb20ScanStatus(auditScanId);
        if (statusData.status === 'COMPLETED') {
          if (statusData.assets) {
            setDiscoveredAssets(statusData.assets);
          }
          setIsAuditing(false);
          setAuditCompleted(true);
        } else if (statusData.status === 'FAILED') {
          setIsAuditing(false);
          setAuditCompleted(true);
          console.error('Audit failed:', statusData.error_detail);
        }
      } catch (err) {
        console.error('Error polling audit status:', err);
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [auditScanId, isAuditing]);

  const handleDiscoveryAudit = async () => {
    setIsAuditing(true);
    setAuditCompleted(false);
    setDiscoveredAssets([]);
    try {
      if (!userId) {
        console.error('No user ID found for audit');
        setIsAuditing(false);
        return;
      }
      const res = await initiateWeb20Scan(userId);
      setAuditScanId(res.scan_id);
    } catch (err) {
      console.error('Error initiating audit:', err);
      setIsAuditing(false);
    }
  };

  const handleTogglePlatform = (platformName: string) => {
    setCheckedPlatforms(prev => ({
      ...prev,
      [platformName]: !prev[platformName]
    }));
  };

  const startPoll = (id: string) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const s = await getWeb20CycleStatus(id);
        setCycleStatus(s.status);
        if (s.articles && s.articles.length > 0) {
          const general = s.articles.find(a => a.platform === 'General') || s.articles[0];
          setArticle(general || null);
          if (general) {
            setGeneratedArticleId(general.id);
          }
        }
        if (s.status !== 'processing') {
          clearInterval(pollRef.current!);
          setGenerating(false);
          if (s.status === 'failed') {
            setGenerateError('Article and image generation failed. Please try again.');
            if (prevArticleRef.current) {
              setArticle(prevArticleRef.current);
              setGeneratedArticleId(prevArticleRef.current.id);
            }
          }
          // Refresh library
          getWeb20Library(userId).then(res => {
            setLibraryListings(res.library || []);
          }).catch(console.error);
        }
      } catch (err: any) {
        clearInterval(pollRef.current!);
        setGenerating(false);
        setCycleStatus('failed');
        setGenerateError(err?.message || 'Failed to poll article generation status. Please try again.');
        if (prevArticleRef.current) {
          setArticle(prevArticleRef.current);
          setGeneratedArticleId(prevArticleRef.current.id);
        }
      }
    }, 3000);
  };

  const handleGenerate = async () => {
    setGenerateError(null);
    prevArticleRef.current = article;
    setGenerating(true); setCycleStatus('processing');
    setArticle(null); setCheckedPlatforms({});
    setGeneratedArticleId(null);
    try {
      const res = await generateWeb20Articles({ user_id: userId, tone: 'professional', primary_keyword: selectedKeyword || undefined });
      setCycleId(res.cycle_id);
      startPoll(res.cycle_id);
    } catch (e: any) {
      setGenerating(false);
      setCycleStatus('failed');
      setGenerateError(e?.message || 'Failed to start article generation. Please try again.');
      if (prevArticleRef.current) {
        setArticle(prevArticleRef.current);
        setGeneratedArticleId(prevArticleRef.current.id);
      }
    }
  };

  const handleMarkComplete = async () => {
    if (!article) return;
    setIsMarkingComplete(true);
    try {
      const selectedPlatforms = Object.entries(checkedPlatforms)
        .filter(([_, checked]) => checked)
        .map(([name, _]) => name);
      const updatedArticle = await completeWeb20Article(article.id, { platforms: selectedPlatforms });
      setArticle(updatedArticle);
      if (drawerArticle?.id === updatedArticle.id) {
        setDrawerArticle(updatedArticle);
      }
      setLibraryListings(prev => prev.map(a => a.id === updatedArticle.id ? updatedArticle : a));
      setMarkCompleteSuccess(true);
      setTimeout(() => setMarkCompleteSuccess(false), 3000);
    } catch (e) {
      console.error(e);
    } finally {
      setIsMarkingComplete(false);
    }
  };

  const handleAudit = async () => {
    const urls = auditUrls.split('\n').map(u => u.trim()).filter(Boolean);
    if (!urls.length || !prefill?.website_url) return;
    setAuditing(true);
    try {
      const res = await auditWeb20Submissions({ article_urls: urls, client_website_url: prefill.website_url, user_id: userId });
      setAuditResults(res.results || []);
      const hist = await getWeb20AuditHistory(userId).catch(() => ({ results: [] }));
      setAuditHistory(hist.results || []);
    } catch {} finally { setAuditing(false); }
  };

  const handleSaveArticleContent = async () => {
    if (!article) return;
    setSavingArticle(true);
    setArticleSaveError(null);
    try {
      const updated = await updateWeb20ArticleContent(article.id, {
        title: editArticleTitle,
        content: editArticleContent,
      });
      setArticle(updated);
      setIsEditingArticle(false);
      setLibraryListings(prev => prev.map(a => a.id === updated.id ? updated : a));
    } catch (e: any) {
      setArticleSaveError(e?.message || 'Failed to save article.');
    } finally {
      setSavingArticle(false);
    }
  };

  const getPlatformGaps = () => {
    if (!prefill?.available_platforms) return [];
    
    const activeDomains = new Set(
      discoveredAssets
        .filter(asset => asset.status === 'ACTIVE' || asset.metadata_json?.audit?.is_live)
        .map(asset => {
          const urlStr = asset.asset_url || '';
          try {
            const host = new URL(urlStr).hostname;
            return host.toLowerCase().replace('www.', '');
          } catch {
            return (asset.platform_domain || '').toLowerCase().replace('www.', '');
          }
        })
    );

    const activePlatformIds = new Set(
      discoveredAssets
        .filter(asset => asset.status === 'ACTIVE' || asset.metadata_json?.audit?.is_live)
        .map(asset => (asset.platform_name || '').toLowerCase())
    );

    return prefill.available_platforms.filter((p: any) => {
      const pName = p.name.toLowerCase();
      const pUrl = (p.url || '').toLowerCase().replace('www.', '');
      return !activePlatformIds.has(pName) && !activeDomains.has(pUrl);
    });
  };

  const statusColor = (s: string) => s === 'Optimal' ? 'text-emerald-500' : s === 'Critical' ? 'text-red-500' : 'text-amber-500';
  const statusBg = (s: string) => s === 'Optimal' ? 'bg-emerald-50 border-emerald-200 dark:bg-emerald-900/20 dark:border-emerald-800' : s === 'Critical' ? 'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800' : 'bg-amber-50 border-amber-200 dark:bg-amber-900/20 dark:border-amber-800';

  if (loading) return (
    <div className={`min-h-screen flex items-center justify-center ${dm ? 'bg-gray-900' : 'bg-gray-50'}`}>
      <div className="flex flex-col items-center gap-3">
        <div className="w-10 h-10 rounded-full border-4 border-blue-600 border-t-transparent animate-spin" />
        <p className="text-sm text-gray-500">Loading module…</p>
      </div>
    </div>
  );

  return (
    <div className={`space-y-6 pb-24 ${dm ? 'dark text-white' : 'text-gray-900'}`}>
      {/* Header */}
      <div>
        <button onClick={onBack} className="flex items-center text-sm font-medium text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400 transition-colors mb-3 cursor-pointer border-none bg-transparent">
          <i className="ri-arrow-left-line mr-1.5" /> Back to Off-Page Content
        </button>
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-sm mr-1">
                <i className="ri-article-line text-white text-sm" />
              </div>
              Web 2.0 / Article Submission
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Generate one SEO article, then post it manually to high-DA blogging platforms</p>
          </div>
          <div className="flex items-center bg-gray-100 dark:bg-gray-800 rounded-xl p-1 border border-gray-200 dark:border-gray-700">
            <button
              onClick={() => websiteState === 'new_website' && setWebsiteState('new_website')}
              disabled={websiteState !== 'new_website'}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all border-none flex items-center gap-1.5 ${
                websiteState === 'new_website'
                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm cursor-default'
                  : 'text-gray-400 dark:text-gray-500 bg-transparent cursor-not-allowed opacity-60'
              }`}
            >
              New Website
              {websiteState !== 'new_website' && <i className="ri-lock-line text-xs" />}
            </button>
            <button
              onClick={() => websiteState === 'existing_website' && setWebsiteState('existing_website')}
              disabled={websiteState !== 'existing_website'}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all border-none flex items-center gap-1.5 ${
                websiteState === 'existing_website'
                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm cursor-default'
                  : 'text-gray-400 dark:text-gray-500 bg-transparent cursor-not-allowed opacity-60'
              }`}
            >
              Existing Website
              {websiteState !== 'existing_website' && <i className="ri-lock-line text-xs" />}
            </button>
          </div>
        </div>
      </div>

      {/* Brand Info Bar */}
      {prefill && (
        <div className={`rounded-2xl border p-4 flex flex-wrap gap-4 items-center ${card}`}>
          <div className="flex items-center gap-2">
            <i className="ri-building-line text-blue-500" />
            <span className="text-sm font-medium">{prefill.brand_name || 'Your Brand'}</span>
          </div>
          <div className="flex items-center gap-2">
            <i className="ri-global-line text-blue-500" />
            <span className="text-sm text-gray-500">{prefill.website_url || '—'}</span>
          </div>
          <div className={`ml-auto px-3 py-1 rounded-full text-xs font-semibold ${prefill.is_new_website ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'}`}>
            {prefill.is_new_website ? `New Domain (${prefill.domain_age_months}mo)` : `Established (${prefill.domain_age_months}mo)`}
          </div>
        </div>
      )}

      {/* Primary Keyword Selection */}
      {prefill?.keywords?.length > 0 && (
        <div className={`rounded-2xl border p-6 ${card}`}>
          <h2 className="text-base font-bold mb-1 flex items-center gap-2">
            <i className="ri-key-2-line text-blue-500" /> Select Primary Keyword
          </h2>
          <p className={`text-sm mb-4 ${dm ? 'text-gray-400' : 'text-gray-500'}`}>
            Choose the keyword the article should be optimised around. One article will be generated per submission.
          </p>
          <div className="flex flex-wrap gap-2">
            {prefill.keywords.map((kw: string) => {
              const isSel = selectedKeyword === kw;
              return (
                <button key={kw} onClick={() => setSelectedKeyword(kw)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium border cursor-pointer transition-colors ${isSel ? 'bg-blue-50 border-blue-300 text-blue-700 dark:bg-blue-900/30 dark:border-blue-700 dark:text-blue-300' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-100 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-700'}`}>
                  <i className={`ri-${isSel ? 'radio-button-fill' : 'checkbox-blank-circle-line'} mr-1`} />
                  {kw}
                </button>
              );
            })}
          </div>
        </div>
      )}


      {/* ─── Audit Existing Web 2.0 Submissions ─── */}
      {websiteState === 'existing_website' && (
        <div className="space-y-6">
          <div className={`rounded-2xl border p-6 ${card}`}>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  <i className="ri-radar-line text-blue-600 dark:text-blue-400"></i>
                  Audit Existing Web 2.0 Submissions
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Use our crawler engine to discover and audit existing Web 2.0 articles published for your brand across the web.
                </p>
              </div>
              <button
                onClick={handleDiscoveryAudit}
                disabled={isAuditing}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-all flex items-center gap-2 border-none cursor-pointer ${
                  isAuditing 
                    ? 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700 text-white shadow-sm'
                }`}
              >
                {isAuditing ? (
                  <><i className="ri-loader-4-line animate-spin"></i> Auditing...</>
                ) : (
                  <><i className="ri-search-eye-line"></i> Run Discovery Audit</>
                )}
              </button>
            </div>

            {isAuditing && (
              <div className="py-8 flex flex-col items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-3"></div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Crawling the web for existing Web 2.0 articles...</p>
              </div>
            )}

            {auditCompleted && discoveredAssets.length === 0 && (
              <div className="py-6 text-center border-t border-gray-100 dark:border-gray-700 mt-4">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gray-50 dark:bg-gray-800 mb-3">
                  <i className="ri-search-line text-xl text-gray-400"></i>
                </div>
                <h4 className="text-sm font-medium text-gray-900 dark:text-white">No existing Web 2.0 articles found</h4>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 max-w-sm mx-auto">
                  No existing Web 2.0 submissions found. Our crawler couldn't find any articles published for your website.
                </p>
              </div>
            )}

            {auditCompleted && discoveredAssets.length > 0 && (
              <div className="mt-4 space-y-6">
                <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                  <table className="w-full text-left border-collapse min-w-[800px]">
                    <thead className="bg-gray-50 dark:bg-gray-800/50">
                      <tr>
                        <th className="py-3 px-4 text-xs font-semibold text-gray-600 dark:text-gray-300">Platform</th>
                        <th className="py-3 px-4 text-xs font-semibold text-gray-600 dark:text-gray-300">Asset Title &amp; URL</th>
                        <th className="py-3 px-4 text-xs font-semibold text-gray-600 dark:text-gray-300">Live</th>
                        <th className="py-3 px-4 text-xs font-semibold text-gray-600 dark:text-gray-300">Backlink</th>
                        <th className="py-3 px-4 text-xs font-semibold text-gray-600 dark:text-gray-300">Google Index</th>
                        <th className="py-3 px-4 text-xs font-semibold text-gray-600 dark:text-gray-300">NAP Check</th>
                        <th className="py-3 px-4 text-xs font-semibold text-gray-600 dark:text-gray-300">Overall Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                      {discoveredAssets.map((asset) => {
                        const audit = asset.metadata_json?.audit;
                        const isLive = audit ? audit.is_live : (asset.status === 'ACTIVE');
                        const backlinkFound = audit?.backlink_found;
                        const backlinkType = audit?.backlink_type;
                        const isIndexed = audit?.is_indexed;
                        const napStatus = audit?.nap_status || 'N/A';
                        const overallStatus = audit?.status || (isLive ? 'Optimal' : 'Critical');
                        const notes = audit?.notes || '';

                        return (
                          <tr key={asset.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors text-sm">
                            <td className="py-3 px-4">
                              <div className="flex items-center gap-2">
                                <div className={`w-6.5 h-6.5 rounded-lg border flex items-center justify-center flex-shrink-0 ${
                                  PLATFORM_COLORS[asset.platform_name] || 'bg-gray-50 dark:bg-gray-800 border-gray-200 text-gray-500'
                                }`}>
                                  <i className={`${PLATFORM_ICONS[asset.platform_name] || 'ri-global-line'} text-sm`} />
                                </div>
                                <span className="font-semibold text-gray-900 dark:text-white capitalize">
                                  {asset.platform_name || asset.platform_domain}
                                </span>
                              </div>
                            </td>
                            <td className="py-3 px-4 max-w-[280px]">
                              <div className="font-medium text-gray-900 dark:text-white truncate" title={asset.asset_title || ''}>
                                {asset.asset_title || 'Untitled Discovery'}
                              </div>
                              <a
                                href={asset.asset_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-blue-600 dark:text-blue-400 font-mono hover:underline flex items-center gap-0.5 truncate mt-0.5"
                              >
                                {asset.asset_url} <i className="ri-external-link-line text-[9px]"></i>
                              </a>
                            </td>
                            <td className="py-3 px-4">
                              {isLive ? (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-emerald-50 dark:bg-emerald-950/20 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-900/50">
                                  Live
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-red-50 dark:bg-red-950/20 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-900/50" title={notes}>
                                  Dead
                                </span>
                              )}
                            </td>
                            <td className="py-3 px-4">
                              {audit ? (
                                backlinkFound ? (
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold border ${
                                    backlinkType === 'dofollow'
                                      ? 'bg-emerald-50 dark:bg-emerald-950/20 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-900/50'
                                      : 'bg-amber-50 dark:bg-amber-955/20 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-900/50'
                                  }`}>
                                    {backlinkType}
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-red-50 dark:bg-red-955/20 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-900/50">
                                    None
                                  </span>
                                )
                              ) : (
                                <span className="text-gray-400 dark:text-gray-500">—</span>
                              )}
                            </td>
                            <td className="py-3 px-4">
                              {audit ? (
                                isIndexed ? (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-emerald-50 dark:bg-emerald-950/20 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-900/50">
                                    Indexed
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-amber-50 dark:bg-amber-955/20 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-900/50">
                                    Not Indexed
                                  </span>
                                )
                              ) : (
                                <span className="text-gray-400 dark:text-gray-500">—</span>
                              )}
                            </td>
                            <td className="py-3 px-4">
                              {audit ? (
                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold border ${
                                  napStatus === 'Consistent'
                                    ? 'bg-emerald-50 dark:bg-emerald-950/20 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-900/50'
                                    : napStatus === 'Inconsistent'
                                    ? 'bg-red-50 dark:bg-red-955/20 text-red-700 dark:text-red-400 border-red-200 dark:border-red-900/50'
                                    : 'bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-700'
                                }`}>
                                  {napStatus}
                                </span>
                              ) : (
                                <span className="text-gray-400 dark:text-gray-500">—</span>
                              )}
                            </td>
                            <td className="py-3 px-4">
                              <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-bold ${statusBg(overallStatus)} ${statusColor(overallStatus)}`}>
                                {overallStatus}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Platform Gaps Panel */}
                {(() => {
                  const gaps = getPlatformGaps();
                  if (gaps.length === 0) return null;
                  return (
                    <div className="p-5 rounded-xl border border-amber-200 dark:border-amber-900/50 bg-amber-50/20 dark:bg-amber-950/5">
                      <div className="flex items-start gap-3">
                        <i className="ri-error-warning-line text-amber-600 dark:text-amber-500 text-lg flex-shrink-0 mt-0.5" />
                        <div>
                          <h4 className="text-sm font-bold text-gray-900 dark:text-white">Platform Checklist Gaps Detected</h4>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            Your backlink profile is currently missing active Web 2.0 articles on these high-authority platforms:
                          </p>
                          <div className="flex flex-wrap gap-2 mt-3">
                            {gaps.map((p: any) => (
                              <span key={p.name} className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-semibold bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300">
                                <div className={`w-4.5 h-4.5 rounded border flex items-center justify-center flex-shrink-0 ${PLATFORM_COLORS[p.name] || 'bg-gray-100 text-gray-500'}`}>
                                  <i className={`${PLATFORM_ICONS[p.name] || 'ri-global-line'} text-[10px]`} />
                                </div>
                                {p.name} (DA {p.da})
                              </span>
                            ))}
                          </div>
                          <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-3 italic">
                            Tip: Switch to the "New Website" view above to generate and submit a unique article to these platforms to fill this gap.
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Failed Generation Alert */}
      {cycleStatus === 'failed' && (
        <div className="p-4 rounded-xl border border-red-200 dark:border-red-900/50 bg-red-50/50 dark:bg-red-950/20 flex items-center justify-between gap-3 text-sm text-red-600 dark:text-red-400 font-medium">
          <div className="flex items-center gap-2">
            <i className="ri-error-warning-line text-lg flex-shrink-0" />
            <span>{generateError || 'Article generation failed. Please check your network and try again.'}</span>
          </div>
          <button
            onClick={() => setCycleStatus('idle')}
            className="text-xs text-red-500 hover:text-red-700 dark:hover:text-red-300 font-bold underline bg-transparent border-none cursor-pointer"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Generate Button */}
      <button onClick={handleGenerate} disabled={generating || cycleStatus === 'processing' || !selectedKeyword}
        className="w-full py-4 rounded-2xl bg-gradient-to-r from-blue-600 to-blue-600 text-white font-bold text-base flex items-center justify-center gap-3 hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg shadow-blue-200 dark:shadow-blue-900/30">
        <i className="ri-sparkling-2-line text-xl" />
        {generating || cycleStatus === 'processing' ? 'Generating Article…' : article ? 'Regenerate Article' : 'Generate Article'}
      </button>

      {/* Loading / Processing spinner (shown only before initial text content is available) */}
      {!article && (generating || cycleStatus === 'processing') && (
        <div className={`flex flex-col items-center justify-center py-16 rounded-2xl border ${card}`}>
          <i className="ri-loader-4-line text-4xl animate-spin text-blue-500 mb-4" />
          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">AI is writing and optimizing your article. This may take up to a minute...</p>
        </div>
      )}

      {/* Generated Articles Library Section */}
      {libraryListings.length > 0 && (
        <div className={`rounded-2xl border p-6 ${card}`}>
          <h3 className="font-bold text-gray-900 dark:text-white text-base flex items-center gap-2">
            <i className="ri-folder-open-line text-blue-600" /> Generated Articles Library
          </h3>
          <p className="text-xs text-gray-400 dark:text-gray-500 mb-4">
            Click an article to view its contents, edit its text, and track platform submission checklist status below.
          </p>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {libraryListings.map((art) => {
              const isSelected = generatedArticleId === art.id;
              const dateStr = art.created_at ? new Date(art.created_at).toLocaleDateString() : '';
              
              return (
                <div
                  key={art.id}
                  onClick={() => {
                    setArticle(art);
                    setGeneratedArticleId(art.id);
                  }}
                  className={`group relative cursor-pointer rounded-xl overflow-hidden border transition-all bg-gray-55 dark:bg-gray-900/50 hover:shadow-md ${
                    isSelected
                      ? 'border-blue-500 ring-2 ring-blue-100 dark:ring-blue-900/30'
                      : 'border-gray-200 dark:border-gray-700 hover:border-blue-400'
                  }`}
                >
                  <div className="aspect-[4/3] bg-gradient-to-br from-blue-500/10 to-indigo-500/10 dark:from-blue-950/20 dark:to-indigo-950/20 relative overflow-hidden flex items-center justify-center border-b border-gray-100 dark:border-gray-800">
                    {art.image_url ? (
                      <img
                        src={art.image_url}
                        alt={art.title || 'Web 2.0 Article'}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <i className="ri-article-line text-4xl text-blue-500 group-hover:scale-110 transition-transform duration-300" />
                    )}
                    {art.published_platforms && art.published_platforms.length > 0 && (
                      <span className="absolute top-2 right-2 px-1.5 py-0.5 bg-emerald-500 text-white rounded text-[8px] font-bold uppercase flex items-center gap-0.5 z-10">
                        <i className="ri-check-line" /> {art.published_platforms.length} Platforms
                      </span>
                    )}
                  </div>
                  <div className="p-3">
                    <p className="text-xs font-bold text-gray-900 dark:text-white truncate" title={art.title || 'Web 2.0 Article'}>
                      {art.title || 'Web 2.0 Article'}
                    </p>
                    <div className="flex items-center justify-between mt-1 text-[10px] text-gray-550 dark:text-gray-400">
                      <span className="truncate">{dateStr}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Generated Article + Platform Tracker */}
      {article && (
        <div className="space-y-4">
          {/* Article Card */}
          <div className={`rounded-2xl border p-5 ${card}`}>
            {/* Image Frame */}
            {article.image_url ? (
              <div className="relative mb-5 overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-900 group flex items-center justify-center min-h-[16rem]">
                {/* Blurred Background for Premium Look */}
                <div 
                  className="absolute inset-0 opacity-40 blur-xl scale-110" 
                  style={{ backgroundImage: `url(${article.image_url})`, backgroundSize: 'cover', backgroundPosition: 'center' }} 
                />
                <img
                  src={article.image_url}
                  alt={article.title}
                  className="relative z-10 max-w-full max-h-72 object-contain rounded-md shadow-2xl transition-transform duration-500 group-hover:scale-[1.02]"
                />
                <button
                  onClick={async (e) => {
                    e.stopPropagation();
                    try {
                      await downloadS3ImageProxy(article.image_url!);
                    } catch (error) {
                      console.error('Download proxy failed', error);
                      alert('Failed to download image. Please try again.');
                    }
                  }}
                  className="absolute top-4 right-4 z-20 p-2.5 bg-white/90 dark:bg-gray-800/90 hover:bg-white dark:hover:bg-gray-700 text-gray-800 dark:text-gray-100 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-all transform translate-y-2 group-hover:translate-y-0 cursor-pointer"
                  title="Download Image"
                >
                  <i className="ri-download-2-line text-xl" />
                </button>
              </div>
            ) : cycleStatus === 'processing' || generating ? (
              <div className="mb-4 h-48 sm:h-56 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 flex flex-col items-center justify-center gap-2 animate-pulse">
                <i className="ri-loader-4-line text-2xl animate-spin text-blue-500" />
                <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">Generating contextual AI image...</span>
              </div>
            ) : (
              <div className="mb-4 h-24 rounded-xl border border-red-200 dark:border-red-900/50 bg-red-50/50 dark:bg-red-950/20 flex items-center justify-center gap-2 text-xs text-red-600 dark:text-red-400 font-medium">
                <i className="ri-error-warning-line text-lg" />
                Image generation failed
              </div>
            )}

            <div className="flex items-start justify-between gap-2 mb-3">
              <div>
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="px-2 py-0.5 rounded-md text-xs font-bold bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">✓ Generated</span>
                  {article.is_unique && <span className="px-2 py-0.5 rounded-md text-xs bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">Unique Content</span>}
                </div>
                {!isEditingArticle && (
                  <h3 className="font-bold text-sm leading-tight">{article.title}</h3>
                )}
              </div>
              {!isEditingArticle && (
                <button
                  onClick={() => {
                    setEditArticleTitle(article.title);
                    setEditArticleContent(article.content);
                    setIsEditingArticle(true);
                    setArticleSaveError(null);
                  }}
                  className="text-gray-400 hover:text-blue-500 transition-colors bg-transparent border-none cursor-pointer flex-shrink-0"
                  title="Edit Article"
                >
                  <i className="ri-pencil-line text-lg" />
                </button>
              )}
            </div>

            {isEditingArticle ? (
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Title</label>
                  <input
                    type="text"
                    value={editArticleTitle}
                    onChange={(e) => setEditArticleTitle(e.target.value)}
                    className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg text-sm font-bold text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Content</label>
                  <textarea
                    value={editArticleContent}
                    onChange={(e) => setEditArticleContent(e.target.value)}
                    rows={12}
                    className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg text-sm text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
                  />
                </div>
                {articleSaveError && (
                  <p className="text-xs text-red-600 dark:text-red-400">{articleSaveError}</p>
                )}
                <div className="flex gap-2">
                  <button
                    onClick={handleSaveArticleContent}
                    disabled={savingArticle}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-4 py-2 text-xs font-semibold cursor-pointer border-none flex justify-center items-center gap-2"
                  >
                    {savingArticle ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-save-3-line" />} Save Changes
                  </button>
                  <button
                    onClick={() => setIsEditingArticle(false)}
                    disabled={savingArticle}
                    className={`rounded-lg px-4 py-2 text-xs font-semibold cursor-pointer border-none ${dm ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-800'}`}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-3 text-xs text-gray-500 mb-3">
                  <span><i className="ri-text-wrap mr-1" />{article.word_count} words</span>
                  {article.backlink_url && <span><i className="ri-link-m mr-1" />Backlink: {article.backlink_url}</span>}
                </div>
                <button onClick={() => setDrawerArticle(article)}
                  className={`w-full rounded-lg px-4 py-2.5 text-xs font-semibold border flex items-center justify-center gap-2 transition-colors ${dm ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
                  <i className="ri-eye-line" /> View Full Article &amp; Copy Content
                </button>
              </>
            )}
          </div>

          {/* Platform Submission Checklist */}
          <div className={`rounded-2xl border p-6 ${card}`}>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  <i className="ri-global-line text-blue-500" /> Suggested Platforms — Submission Checklist
                </h3>
                <p className={`text-xs mt-1 ${dm ? 'text-gray-400' : 'text-gray-500'}`}>
                  Copy the article above and post it manually on each platform. Check them off once done.
                </p>
              </div>
              <div className="flex items-center gap-2">
                {markCompleteSuccess && (
                  <span className="text-xs font-bold text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
                    <i className="ri-checkbox-circle-fill text-base" /> Saved!
                  </span>
                )}
                <button
                  onClick={handleMarkComplete}
                  disabled={isMarkingComplete || !Object.values(checkedPlatforms).some(Boolean)}
                  className={`px-4 py-2 text-xs font-bold rounded-xl flex items-center gap-1.5 transition-all shadow-sm cursor-pointer border-none ${
                    Object.values(checkedPlatforms).some(Boolean)
                      ? 'bg-blue-600 hover:bg-blue-700 text-white hover:shadow'
                      : 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-600 cursor-not-allowed shadow-none'
                  }`}
                >
                  {isMarkingComplete ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" /> Saving...
                    </>
                  ) : (
                    <>
                      <i className="ri-checkbox-circle-line" /> Mark as Complete
                    </>
                  )}
                </button>
              </div>
            </div>
            <div className="space-y-3">
              {(prefill?.available_platforms || []).slice(0, 6).map((p: any) => {
                const isChecked = !!checkedPlatforms[p.name];
                return (
                  <div 
                    key={p.name} 
                    className={`flex items-center justify-between p-4 rounded-xl border transition-all ${isChecked ? 'bg-emerald-50/30 dark:bg-emerald-950/10 border-emerald-100 dark:border-emerald-900/50' : dm ? 'bg-gray-900/50 border-gray-800' : 'bg-gray-55 border-gray-100'}`}
                  >
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => handleTogglePlatform(p.name)}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                      />
                      <div className={`w-8 h-8 rounded-lg border flex items-center justify-center flex-shrink-0 ${PLATFORM_COLORS[p.name] || 'bg-gray-100 dark:bg-gray-800 border-gray-200 text-gray-600'}`}>
                        <i className={`${PLATFORM_ICONS[p.name] || 'ri-global-line'} text-base`} />
                      </div>
                      <div>
                        <span className={`text-sm font-semibold text-gray-900 dark:text-white capitalize ${isChecked ? 'line-through text-gray-400 dark:text-gray-500 font-medium' : ''}`}>{p.name}</span>
                        <span className={`ml-2 px-2 py-0.5 rounded-full text-[10px] font-bold ${p.da >= 93 ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'}`}>DA {p.da}</span>
                      </div>
                    </div>
                    
                    <a
                      href={`https://${p.url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1 font-semibold"
                    >
                      Visit Site <i className="ri-external-link-line" />
                    </a>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}


      {/* Article Drawer */}
      {drawerArticle && (
      <ArticleDrawer
          article={drawerArticle}
          onClose={() => setDrawerArticle(null)}
          onSaved={(updated) => {
            setArticle(updated);
            setDrawerArticle(updated);
            setLibraryListings(prev => prev.map(a => a.id === updated.id ? updated : a));
          }}
          darkMode={dm}
        />
      )}
    </div>
  );
}
